import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { RouterOutputs } from '../../lib/trpc';
import { Panel } from '../common/Panel';
import {
  axisProps,
  CHART_MARGIN,
  formatCompactKr,
  formatWholeKr,
  gridProps,
  tooltipContentStyle,
  tooltipItemStyle,
  tooltipLabelStyle,
  useIsSmUp,
} from './chart';

type Trends = RouterOutputs['insights']['trends'];

const SERIES = [
  { key: 'Inkomst', color: 'var(--chart-1)' },
  { key: 'Utgifter', color: 'var(--chart-3)' },
] as const;

/** Income and spending for the last six salary periods. */
export function TrendCard({ trends }: { trends: Trends | undefined }) {
  const isSmUp = useIsSmUp();
  const data =
    trends?.months.map((m) => ({
      // "okt 2026" → "okt" on phones; the tooltip keeps the full label
      label: m.label,
      Inkomst: Math.round(m.income / 100),
      Utgifter: Math.round(m.totalSpending / 100),
    })) ?? [];

  return (
    <Panel title="Senaste sex perioderna" description="Inkomst och utgifter per löneperiod">
      {data.length > 0 ? (
        <>
          <div className="mb-2 flex gap-4 text-xs text-muted-foreground">
            {SERIES.map((s) => (
              <span key={s.key} className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden
                  className="size-2.5 rounded-sm"
                  style={{ backgroundColor: s.color }}
                />
                {s.key}
              </span>
            ))}
          </div>
          <ResponsiveContainer width="100%" height={isSmUp ? 260 : 200}>
            <BarChart data={data} margin={CHART_MARGIN} barGap={2}>
              <CartesianGrid {...gridProps} vertical={false} />
              <XAxis
                {...axisProps}
                dataKey="label"
                interval={0}
                tickFormatter={(label: string) => (isSmUp ? label : (label.split(' ')[0] ?? label))}
              />
              <YAxis {...axisProps} width={isSmUp ? 48 : 36} tickFormatter={formatCompactKr} />
              <Tooltip
                formatter={(value, name) => [formatWholeKr(value), name]}
                contentStyle={tooltipContentStyle}
                itemStyle={tooltipItemStyle}
                labelStyle={tooltipLabelStyle}
                cursor={{ fill: 'var(--muted)', opacity: 0.6 }}
              />
              {SERIES.map((s) => (
                <Bar key={s.key} dataKey={s.key} fill={s.color} radius={[3, 3, 0, 0]} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </>
      ) : (
        <p className="py-8 text-center text-sm text-muted-foreground">Ingen historik än.</p>
      )}
    </Panel>
  );
}
